<template>
  <div class="animated fadeIn">
    <b-row>
      <b-col xl="6" md="12">
        <draggable
          v-model="leftCards"
          group="profile-cards"
          handle=".drag-handle"
          @end="saveOrder"
          :animation="200"
          ghost-class="ghost-card"
        >
          <div v-for="card in leftCards" :key="card.id" class="mb-3">
            <component
              :is="card.component"
              :userData="userData"
              :authUser="authUser"
              :servers="servers"
              :userLock="userLock"
              :note="userData ? userData.note : ''"
              :height="card.height"
              @update:height="(h) => updateHeight(card, h)"
            />
          </div>
        </draggable>
      </b-col>

      <b-col xl="6" md="12">
        <draggable
          v-model="rightCards"
          group="profile-cards"
          handle=".drag-handle"
          @end="saveOrder"
          :animation="200"
          ghost-class="ghost-card"
        >
          <div v-for="card in rightCards" :key="card.id" class="mb-3">
            <component
              :is="card.component"
              :userData="userData"
              :authUser="authUser"
              :servers="servers"
              :userLock="userLock"
              :note="userData ? userData.note : ''"
              :height="card.height"
              @update:height="(h) => updateHeight(card, h)"
            />
          </div>
        </draggable>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import Servers from '../../../_servers'
import GeneralInfoCard from './cards/GeneralInfoCard'
import AccountActionsCard from './cards/AccountActionsCard'
import IPHistoryCard from './cards/IPHistoryCard'
import BanHistoryCard from './cards/BanHistoryCard'
import PvP from './pvp'
import UserNotes from './UserNotes.vue'
import BanManagement from './BanManagement.vue'
import draggable from 'vuedraggable'
import { mapGetters, mapActions } from 'vuex'
import VueNotifications from 'vue-notifications'

export default {
  name: 'Base',
  components: {
    GeneralInfoCard,
    AccountActionsCard,
    IPHistoryCard,
    BanHistoryCard,
    PvP,
    UserNotes,
    BanManagement,
    draggable
  },
  computed: {
    ...mapGetters({
      userData: 'userData',
      authUser: 'authUser',
      userError: 'userError',
      userLock: 'userLock',
      newUsername: 'newUsername'
    })
  },
  data () {
    return {
      servers: Servers,
      leftCards: [],
      rightCards: []
    }
  },
  created () {
    this.loadOrder()
  },
  watch: {
    userError: function (e) {
      this.serverError(e)
    }
  },
  methods: {
    ...mapActions(['setLock', 'setUsername']),
    updateHeight(card, newHeight) {
      card.height = newHeight;
      this.saveOrder();
    },
    loadOrder() {
      const saved = localStorage.getItem('modtools_profile_layout');
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          this.leftCards = parsed.left;
          this.rightCards = parsed.right;
          // Ensure defaults if missing
          this.leftCards.forEach(c => { if(!c.height && c.id==='ips') c.height=500; });
          return;
        } catch(e) {
          console.error("Failed to load layout", e);
        }
      }
      // Default Layout
      this.leftCards = [
        { id: 'general', component: 'GeneralInfoCard' },
        { id: 'actions', component: 'AccountActionsCard' },
        { id: 'pvp', component: 'PvP' },
        { id: 'notes', component: 'UserNotes' }
      ];
      this.rightCards = [
        { id: 'ips', component: 'IPHistoryCard', height: 500 },
        { id: 'infractions', component: 'BanHistoryCard', height: 250 },
        { id: 'ban_management', component: 'BanManagement' }
      ];
    },
    saveOrder() {
      const layout = {
        left: this.leftCards,
        right: this.rightCards
      };
      localStorage.setItem('modtools_profile_layout', JSON.stringify(layout));
    },
    serverError (val) {
      switch (val.error) {
        case 3:
          this.UsernameTaken()
          break
        case 1:
          this.ChangeSuccessful()
          break
        case 2:
          this.UserOnline()
          break
        case 5:
          this.TransferErrorNotMerged()
          break
        case 7:
          this.OpenLendingTrades()
          break
        case 4:
          this.UserNotFound()
          return
        case 2002:
          this.UnmergeTradeError()
          break
        case 2003:
          this.UnmergeError()
          break
        case 2010:
          this.UnmergeTransferError()
          break
      }
      if (val.error === 1 && this.newUsername !== null) {
        this.$router.push('/user/' + this.newUsername + '/profile')
        this.emit('getUserData', {'username': this.newUsername, 'server': this.$route.query.server})
      } else {
        this.emit('getUserData', {'username': this.$route.params.username, 'server': this.$route.query.server})
      }
      this.setUsername(null)
      this.setLock(false)
    },
  },
  notifications: {
    ChangeSuccessful: {
      type: VueNotifications.types.success,
      title: 'Change successful!',
      message: 'Change was successful!'
    },
    UsernameTaken: {
      type: VueNotifications.types.error,
      title: 'Rename failed!',
      message: 'This username is already taken.'
    },
    UserOnline: {
      type: VueNotifications.types.error,
      title: 'Change failed!',
      message: 'Change failed, this user is currently online.'
    },
    UserNotFound: {
      type: VueNotifications.types.error,
      title: 'Username not found!',
      message: 'Username doesn\'t exist.'
    },
    TransferProgress: {
      type: VueNotifications.types.info,
      title: 'Transfer in progress!',
      message: 'This may take several seconds, please be patient.'
    },
    NotMerged: {
      type: VueNotifications.types.error,
      title: 'This account was not merged yet!',
      message: 'You might want to check Red and Blue!'
    },
    TransferErrorNotMerged: {
      type: VueNotifications.types.error,
      title: 'Server Transfer failed!',
      message: ' This account was not merged yet!'
    },
    UnmergeError: {
      type: VueNotifications.types.error,
      title: 'Unmerge failed!',
      message: 'Unmerge failed somehow!'
    },
    UnmergeTradeError: {
      type: VueNotifications.types.error,
      title: 'Unmerge failed!',
      message: 'This user has trades on Silver.'
    },
    UnmergeTransferError: {
      type: VueNotifications.types.error,
      title: 'Unmerge failed!',
      message: 'This user already transferred servers.'
    },
    OpenLendingTrades: {
      type: VueNotifications.types.error,
      title: 'Transfer failed!',
      message: 'You cannot transfer a user that has a pending lending trade.'
    }
  }
}
</script>

<style scoped>
.ghost-card {
  opacity: 0.5;
  background: #c8ebfb;
  border: 2px dashed #20a8d8;
}

/deep/ .drag-handle {
  font-size: 0.75rem;
  opacity: 0.3;
  transition: all 0.2s;
  cursor: grab;
  position: relative;
  display: inline-block;
}

/deep/ .drag-handle:hover {
  opacity: 1;
  color: #20a8d8;
}

/* Invisible hitbox expansion for easier dragging on mobile */
/deep/ .drag-handle::after {
  content: '';
  position: absolute;
  top: -20px;
  bottom: -20px;
  left: -20px;
  right: -20px;
  z-index: 10;
}
</style>
